'use client'

import React from 'react'
import { ReactNode } from 'react'
import styled, { css } from 'styled-components'

import { TypeStyle } from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { fromMd, untilMd } from '@syconium/little-miss-figgy/dist/constants/breakpoints'

import {
  PageTileSectionTileTextAlignment,
  PageTileSectionTileTextPlacement,
} from '../../../../../__generated__/graphql/catalog/graphql'
import { Tile as BaseTile } from '../../../tiles/Tiles.client'
import { ContentfulStyledLink } from '../components/ContentfulStyledLink'

import { SectionAlignment } from './ContentfulPageTileSection.client'

type TitleBaseProps = {
  $textAlign?: string
  $mobileTextAlign?: string
  $hasHorizontalMargin?: boolean
}

type StyledButtonRowProps = {
  $textAlignment: 'left' | 'center' | 'right'
}

const TitleBase = css<TitleBaseProps>`
  margin-bottom: 16px;
  padding: 0 ${o => o.$hasHorizontalMargin && o.theme.dimensions.layout.sidePadding.md + 'px'};
  text-align: ${props => props.$mobileTextAlign ?? 'left'};

  ${fromMd} {
    text-align: ${props => props.$textAlign ?? 'left'};
    margin-bottom: 24px;
  }
`
const TitleDefault = styled(TypeStyle.HeadlineSmall)<TitleBaseProps>`
  ${TitleBase}
`

const TitleMedium = styled(TypeStyle.HeadlineMedium)<TitleBaseProps>`
  ${TitleBase}
`

const TitleLarge = styled(TypeStyle.HeadlineLarge)<TitleBaseProps>`
  ${TitleBase}
`

const TitleExtraLarge = styled(TypeStyle.HeadlineExtraLarge)<TitleBaseProps>`
  ${TitleBase}
`
export const PageTileSectionTitle: React.FC<
  {
    $titleTextSize: 'h1' | 'h2' | 'h3' | 'h4'
    children: string | ReactNode
  } & TitleBaseProps
> = ({ $titleTextSize, ...props }) => {
  switch ($titleTextSize) {
    case 'h1':
      return (
        <TitleExtraLarge asTag={$titleTextSize} {...props}>
          {props.children}
        </TitleExtraLarge>
      )
    case 'h2':
      return (
        <TitleLarge asTag={$titleTextSize} {...props}>
          {props.children}
        </TitleLarge>
      )
    case 'h3':
      return (
        <TitleMedium asTag={$titleTextSize} {...props}>
          {props.children}
        </TitleMedium>
      )
    default:
      return (
        <TitleDefault asTag={$titleTextSize} {...props}>
          {props.children}
        </TitleDefault>
      )
  }
}

export const Container = styled.div<{
  $textAlign: SectionAlignment
  $hasHover: boolean
  $hasVerticalMargin?: boolean
  $hasOverlay?: boolean
  $shouldUseCollectionStyling?: boolean
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${o => (o.$hasVerticalMargin ? '2px 0' : '0')};
  text-align: ${o => o.$textAlign};

  ${o =>
    o.$hasHover &&
    `
      &:hover ${Image},
      {
        display: none;
      }
      `}
`

export const ImageWrap = styled.figure<{
  $aspectRatios: { sm: number; md: number }
  $roundCorners: { sm: boolean; md: boolean }
  $hasOverlay?: boolean
  $shouldUseCollectionStyling?: boolean
  $isTwoUp?: boolean
}>`
  display: block;
  overflow: hidden;
  ${o => !o.$isTwoUp && `padding-top: ${(1 / o.$aspectRatios.sm) * 100}%;`}
  ${o => o.$isTwoUp && `aspect-ratio: ${o.$aspectRatios.sm};`}
  position: relative;
  width: 100%;

  ${untilMd} {
    ${o =>
      o.$isTwoUp &&
      !o.$hasOverlay &&
      `
      max-height: 350px;
      & > img,
      & > video {
        max-height: 350px;
      }
    `}
  }

  & > img,
  & > video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    font-family: 'object-fit: cover;';
  }

  ${fromMd} {
    ${o => !o.$isTwoUp && `padding-top: ${(1 / o.$aspectRatios.md) * 100}%;`}
    ${o => o.$isTwoUp && `aspect-ratio: ${o.$aspectRatios.md};`}
    border-radius: ${o => o.$roundCorners.md && '6px'};
    ${o => o.$isTwoUp && !o.$hasOverlay && `max-height: 650px;`}
    ${o =>
      o.$isTwoUp &&
      !o.$hasOverlay &&
      `
      max-height: 650px;
      & > img,
      & > video {
        max-height: 650px;
      }
    `}
  }

  ${o => o.$shouldUseCollectionStyling && 'aspect-ratio: 4/3;'}
`

export const TileConfiguredOverlay = styled.div<{
  $placement: PageTileSectionTileTextPlacement | null
  $textAlignment: PageTileSectionTileTextAlignment | null
  $hexOverlayOpacity: string | null
}>`
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: ${o => o.theme.spacing(5)};

  align-items: ${o =>
    o.$textAlignment === 'center'
      ? 'center'
      : o.$textAlignment === 'right'
      ? 'flex-end'
      : 'flex-start'};

  ${o =>
    o.$hexOverlayOpacity
      ? `background-color: ${o.theme.color.fill.background + o.$hexOverlayOpacity};`
      : null}

  justify-content: ${o => {
    switch (o.$placement) {
      case 'overlayTop':
        return 'flex-start'
      case 'overlayCenter':
        return 'center'
      case 'overlayBottom':
        return 'flex-end'
      default:
        return 'flex-end'
    }
  }};

  ${fromMd} {
    padding: ${o => o.theme.spacing(10)};
  }
`

export const TileConfiguredOverlayContent = styled.div<{
  $alignment: PageTileSectionTileTextAlignment
}>`
  text-align: ${o => o.$alignment};
  box-sizing: border-box;
  max-width: 650px;
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(4)};
`

export const TileConfiguredTextBelow = styled.div<{
  $textAlignment: PageTileSectionTileTextAlignment
  $isTwoUp?: boolean
}>`
  color: ${o => o.theme.color.text.primary.on.background};
  align-items: ${o =>
    o.$textAlignment === 'center'
      ? 'center'
      : o.$textAlignment === 'right'
      ? 'flex-end'
      : 'flex-start'};

  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(2.5)};
  margin-top: ${o => o.theme.spacing(4)};
  ${o => o.$isTwoUp && `margin: ${o.theme.spacing(5)};`}

  ${fromMd} {
    margin: ${o => o.$isTwoUp && o.theme.spacing(10)};
  }
`

export const StyledContentfulStyledLink = styled(ContentfulStyledLink)<{ $isButtonLink?: boolean }>`
  display: inline-flex;
  text-transform: uppercase;
  ${o => o.theme.typography.effect.uppercase};
  ${untilMd} {
    ${o => o.$isButtonLink && `padding: 0 ${o.theme.spacing(2)};`}
  }
`
export const Tile = styled(BaseTile)`
  position: relative;
  min-width: 204px;
`

export const StyledButtonRow = styled.div<StyledButtonRowProps>`
  display: flex;
  justify-content: ${o =>
    o.$textAlignment === 'center'
      ? 'center'
      : o.$textAlignment === 'right'
      ? 'flex-end'
      : 'flex-start'};
`

export const StyledTileTextContent = styled.div<{ $isOverlay: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(4)};
  color: ${o => o.$isOverlay && o.theme.color.text.highContrast.on.subtle};
`
